/**
 * SupervisionAlertsBanner — aggregates the highest-severity items
 * across all accounts into a stack of clean alert cards. Never
 * invents alerts: if no data has been collected, renders an
 * explicit "Data unavailable" state.
 *
 * Categories are normalized to:
 *   Normal · Monitoring · Limited · Needs Review · Locked Out · Halted · Data Unavailable
 *
 * Read-only. Never displays raw JSON or raw ISO timestamps.
 */
import React from 'react';
import {
  AlertOctagon,
  AlertTriangle,
  CheckCircle2,
  Eye,
  Loader2,
  Lock,
  Shield,
} from 'lucide-react';
import {
  fmtRelative,
  fmtEtTime,
  severityToClass,
  severityToLabel,
  Severity,
} from './formatters';
import {
  SupervisionAccount,
  useAccountSupervision,
} from '../../hooks/useAccountSupervision';

interface Alert {
  severity: Severity;
  account: string;
  account_id: string;
  reason: string;
  next_action: string | null;
  last_update_iso: string | null;
}

function accountSeverity(a: SupervisionAccount): Severity {
  const nt = (a.no_trade_status || '').toUpperCase();
  const rs = (a.risk_status || '').toUpperCase();
  const rv = (a.review_status || '').toUpperCase();

  if (nt === 'EMERGENCY' || rs === 'RED') return 'halt';
  if (nt === 'CRITICAL') return 'lock';
  if (rs === 'YELLOW' || nt === 'WARNING') return 'limited';
  if (rv === 'REVIEW_OVERDUE' || rv === 'NEVER_REVIEWED') return 'review';
  if (a.blockers && a.blockers.length > 0) return 'monitoring';
  if (nt === 'HEALTHY' && rs === 'GREEN' && rv === 'CURRENT') return 'ok';
  return 'monitoring';
}

function alertsFor(a: SupervisionAccount): Alert[] {
  const out: Alert[] = [];
  const sev = accountSeverity(a);
  const lastUpdate = a.last_review_at || a.last_fill_at || a.last_trade_at || a.last_order_at;

  if (sev === 'halt') {
    out.push({
      severity: 'halt',
      account: a.account_label,
      account_id: a.account_id_masked,
      reason:
        a.no_trade_status === 'EMERGENCY'
          ? 'Account flagged EMERGENCY no-trade state.'
          : 'Account risk state is RED.',
      next_action:
        a.action_required && a.action_required[0]
          ? a.action_required[0]
          : 'Pause trading and escalate to operator.',
      last_update_iso: lastUpdate,
    });
  } else if (sev === 'lock') {
    out.push({
      severity: 'lock',
      account: a.account_label,
      account_id: a.account_id_masked,
      reason: 'Account flagged CRITICAL no-trade state.',
      next_action:
        a.action_required && a.action_required[0]
          ? a.action_required[0]
          : 'Resolve critical no-trade gate before next session.',
      last_update_iso: lastUpdate,
    });
  } else if (sev === 'limited') {
    out.push({
      severity: 'limited',
      account: a.account_label,
      account_id: a.account_id_masked,
      reason:
        a.risk_status === 'YELLOW'
          ? 'Account risk state is YELLOW.'
          : 'Account flagged WARNING no-trade state.',
      next_action:
        a.action_required && a.action_required[0]
          ? a.action_required[0]
          : 'Review thresholds and recent fills.',
      last_update_iso: lastUpdate,
    });
  } else if (sev === 'review') {
    out.push({
      severity: 'review',
      account: a.account_label,
      account_id: a.account_id_masked,
      reason:
        (a.review_status || '').toUpperCase() === 'NEVER_REVIEWED'
          ? 'Account has never been reviewed by the Council.'
          : 'Council review is overdue.',
      next_action:
        a.action_required && a.action_required[0]
          ? a.action_required[0]
          : 'Schedule a Council review.',
      last_update_iso: lastUpdate,
    });
  } else if (sev === 'monitoring' && a.blockers && a.blockers.length > 0) {
    out.push({
      severity: 'monitoring',
      account: a.account_label,
      account_id: a.account_id_masked,
      reason: `Blockers present: ${a.blockers.slice(0, 2).join('; ')}`,
      next_action:
        a.action_required && a.action_required[0]
          ? a.action_required[0]
          : 'Clear blockers before next session.',
      last_update_iso: lastUpdate,
    });
  }
  return out;
}

function severityOrder(s: Severity): number {
  // halt highest priority
  const order: Severity[] = ['halt', 'lock', 'limited', 'review', 'monitoring', 'ok', 'unknown'];
  return order.indexOf(s);
}

function severityIcon(s: Severity): React.ReactElement {
  switch (s) {
    case 'halt':
      return <AlertOctagon size={14} className="text-rose-600" />;
    case 'lock':
      return <Lock size={14} className="text-rose-600" />;
    case 'limited':
      return <AlertTriangle size={14} className="text-amber-600" />;
    case 'review':
      return <Eye size={14} className="text-amber-600" />;
    case 'monitoring':
      return <Shield size={14} className="text-sky-600" />;
    case 'ok':
      return <CheckCircle2 size={14} className="text-emerald-600" />;
    default:
      return <Shield size={14} className="text-[var(--muted-text)]" />;
  }
}

export default function SupervisionAlertsBanner() {
  const { status, loading, error } = useAccountSupervision();

  if (loading) {
    return (
      <section
        data-testid="supervision-alerts-loading"
        className="border border-[var(--card-border)] bg-[var(--card-bg)] rounded-2xl p-4"
      >
        <div className="flex items-center gap-2 text-[var(--muted-text)]">
          <Loader2 size={14} className="animate-spin" />
          <span className="text-[11px] uppercase tracking-widest font-black">
            Loading account supervision alerts…
          </span>
        </div>
      </section>
    );
  }

  if (!status || !Array.isArray(status.accounts) || status.accounts.length === 0) {
    return (
      <section
        data-testid="supervision-alerts-unavailable"
        className="border border-[var(--card-border)] bg-[var(--card-bg)] rounded-2xl p-4"
      >
        <h3 className="text-sm font-black uppercase italic text-[var(--app-text)] flex items-center gap-2">
          <Shield size={14} /> Account Supervision
        </h3>
        <div className="mt-2 text-xs text-[var(--muted-text)]">
          Account supervision has not produced a run yet.{' '}
          <span
            className={`inline-flex items-center gap-1 ml-2 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest rounded-md border ${severityToClass(
              'unknown',
            )}`}
          >
            Source: UNAVAILABLE
          </span>
          {error && (
            <div className="mt-1 text-[10px] text-amber-600">
              Last error: {error}
            </div>
          )}
        </div>
      </section>
    );
  }

  // Build alerts list. If everything is normal, show one calm "All accounts normal" card.
  const alerts: Alert[] = [];
  for (const a of status.accounts) alerts.push(...alertsFor(a));
  alerts.sort((x, y) => severityOrder(x.severity) - severityOrder(y.severity));

  const allOk = alerts.length === 0;
  const counts = status.counts || ({} as any);
  const total = counts.total ?? status.accounts.length;

  return (
    <section
      data-testid="supervision-alerts"
      className="border border-[var(--card-border)] bg-[var(--card-bg)] rounded-2xl p-4"
    >
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-sm font-black uppercase italic text-[var(--app-text)] flex items-center gap-2">
          <Shield size={14} /> Account Supervision Alerts
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)]">
            {total} account{total === 1 ? '' : 's'} monitored
          </span>
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest rounded-md border ${severityToClass(
              allOk ? 'ok' : 'monitoring',
            )}`}
            data-testid="supervision-alerts-source"
          >
            Source: LIVE
          </span>
        </div>
      </header>

      {allOk ? (
        <div
          data-testid="supervision-alerts-all-ok"
          className={`mt-3 border rounded-xl p-3 ${severityToClass('ok')}`}
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 size={14} />
            <span className="text-xs font-black uppercase italic">
              All accounts normal · {severityToLabel('ok')}
            </span>
          </div>
          <p className="mt-1 text-[11px]">
            No critical or warning alerts in the latest supervision run.
            Last run completed {fmtRelative(status.finished_at)}.
          </p>
        </div>
      ) : (
        <ul className="mt-3 space-y-2">
          {alerts.map((a, i) => (
            <li
              key={`${a.account_id}-${a.severity}-${i}`}
              data-testid={`supervision-alert-${a.severity}-${a.account_id}`}
              className={`border rounded-xl p-3 ${severityToClass(a.severity)}`}
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  {severityIcon(a.severity)}
                  <div className="min-w-0">
                    <div className="text-[12px] font-black italic truncate">
                      {a.account}
                    </div>
                    <div className="text-[9px] font-bold uppercase tracking-widest opacity-80">
                      {a.account_id}
                    </div>
                  </div>
                </div>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-1 text-[9px] font-black uppercase tracking-widest rounded-md border ${severityToClass(
                    a.severity,
                  )}`}
                >
                  {severityToLabel(a.severity)}
                </span>
              </div>
              <p className="mt-2 text-[11px] italic">{a.reason}</p>
              {a.next_action && (
                <div className="mt-1 text-[10px]">
                  <span className="font-black uppercase tracking-widest mr-1">
                    Next:
                  </span>
                  {a.next_action}
                </div>
              )}
              {a.last_update_iso && (
                <div className="mt-1 text-[10px] opacity-80">
                  <span className="font-black uppercase tracking-widest mr-1">
                    Last update:
                  </span>
                  {fmtRelative(a.last_update_iso)} ({fmtEtTime(a.last_update_iso)})
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <p className="mt-3 text-[10px] text-[var(--muted-text)] italic">
        Live trading remains blocked at the platform level. This panel is
        read-only.
      </p>
    </section>
  );
}
